import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sha256Hex } from '../../utils/sha256';
import { MICROGRAPH_TYPES, registrationExtras, uploadMicrograph } from '../../utils/uploadMicrograph';
import { errorText } from '../../utils/errorText';
import { input, label } from '../shared/styles';

/*
 * Add a microscopy image (utils/uploadMicrograph.js: hashed here, uploaded to
 * the presigned address, registered with what it shows; the server reads its
 * metadata and scale).
 *
 * The scan station gives only the link. The hub's micrograph form also says
 * what the image shows (`structure`, one of the server's STRUCTURES), the
 * magnification it was taken at and a note; each goes into the registration
 * body only when given, so the station's registrations are unchanged.
 */
// utils/sha256 falls back to plain JS where crypto.subtle is missing -- a phone
// opening the app over http://192.168.x.x is not a secure context.
export { sha256Hex, registrationExtras };

const TYPES = MICROGRAPH_TYPES;

export default function MicroscopyUpload({ link, instruments = [], onDone, structure, magnification, notes }) {
  const { t } = useTranslation('scan');
  const [file, setFile] = useState(null);
  const [instrumentId, setInstrumentId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const upload = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await uploadMicrograph(file, { links: link ? [link] : [], instrumentId,
                                                  structure, magnification, notes });
      setFile(null);
      onDone?.(data);
    } catch (e) {
      setError(e?.uploadStatus ? t('uploadFailed', { status: e.uploadStatus }) : errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }} data-testid="micro-upload">
      <label style={label}>{t('imageFile')}
        <input type="file" accept={TYPES.join(',')} onChange={(e) => setFile(e.target.files?.[0] || null)}
          title={t('tip.imageFile')} data-testid="micro-file" />
      </label>
      {instruments.length > 0 && (
        <label style={label}>{t('microscope')}
          <select style={input} value={instrumentId} onChange={(e) => setInstrumentId(e.target.value)} title={t('tip.microscope')} data-testid="micro-instrument">
            <option value="">—</option>
            {instruments.map((i) => <option key={i.instrument_id} value={i.instrument_id}>{i.asset_tag} · {i.name}</option>)}
          </select>
        </label>
      )}
      <button type="button" disabled={!file || busy} onClick={upload} title={t('tip.addImage')} data-testid="micro-upload-go">{t('addImage')}</button>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)', width: '100%' }}>{error}</p>}
    </div>
  );
}
